import { ChargePointLog } from '../app/ApiGen';
import { MeterValueMeasurand, SampledValue, SampledValueWithTimestamp, Timestamp } from './meterValues';
import { MeterValuesLogAttributes } from './MeterValuesLog';
import { Normalised } from './request';

export class ChargePointLogs {
  private readonly logs: Normalised<ChargePointLog>[];

  constructor(logs: Normalised<ChargePointLog>[]) {
    this.logs = logs;
  }

  private static isMeterValue(log: Normalised<ChargePointLog>): log is MeterValuesLogAttributes {
    return log.eventPayload !== undefined && 'meterValue' in log.eventPayload && log.eventType === 'MeterValues';
  }

  private static meterValuesForTimestamp(args: {
    meterValues: SampledValue[];
    measurand: MeterValueMeasurand;
    timestamp: Timestamp;
  }) {
    const { measurand, meterValues, timestamp } = args;
    return meterValues.reduce<SampledValueWithTimestamp[]>((sampledValuesForMeasurand, sampledValue) => {
      if (sampledValue.measurand !== measurand) {
        return sampledValuesForMeasurand;
      }
      return [...sampledValuesForMeasurand, { ...sampledValue, timestamp }];
    }, []);
  }

  get length(): number {
    return this.logs.length;
  }

  meterValuesForMeasurand(args: { measurand: MeterValueMeasurand }): SampledValueWithTimestamp[] {
    const { measurand } = args;

    const byTimestamp = this.logs.reduce<Record<Timestamp, SampledValueWithTimestamp[]>>((acc, log) => {
      if (!ChargePointLogs.isMeterValue(log)) {
        return acc;
      }

      const dataByTimestamp = log.eventPayload.meterValue.reduce<Record<Timestamp, SampledValueWithTimestamp[]>>(
        (meterValueAcc, { sampledValue: meterValues, timestamp }) => {
          const previousValuesForTimestamp = meterValueAcc[timestamp] ?? [];
          return {
            [timestamp]: [
              ...previousValuesForTimestamp,
              ...ChargePointLogs.meterValuesForTimestamp({ meterValues, measurand, timestamp }),
            ],
          };
        },
        {},
      );

      return { ...acc, ...dataByTimestamp };
    }, {});

    return Object.values(byTimestamp).flat();
  }

  ofType(eventType: Normalised<ChargePointLog>['eventType']): ChargePointLogs {
    const ofEventType = this.logs.filter((eventValue) => eventValue.eventType.startsWith(eventType));
    return new ChargePointLogs(ofEventType);
  }

  sortByServerDate(): ChargePointLogs {
    const sortedLogs = this.logs.sort((a, b) => {
      if (a.serverEventDate < b.serverEventDate) {
        return -1;
      }
      if (a.serverEventDate > b.serverEventDate) {
        return 1;
      }
      return 0;
    });
    return new ChargePointLogs(sortedLogs);
  }

  toChartData<ChartDatum>(args: { toChartDatum: (log: Normalised<ChargePointLog>) => ChartDatum }): ChartDatum[] {
    const { toChartDatum } = args;
    return this.logs.map(toChartDatum);
  }
}
